import type React from 'react';
import { useCallback } from 'react';
import { ProgressStepper, ProgressStepperStep, ProgressStepperStepLabel } from '@zillow/constellation';
import type { ProgressStepperStepProps } from '@zillow/constellation';
import { getOverviewFromApplication } from '../../utils/overview/overview-statuses';
import { FIELD_IDS, getSectionIndex, REVIEW_STATUS, SECTION_IDS, type SectionId } from '../../constants/wizard';
import { useApplicationContext } from '../../contexts/ApplicationContext';
import { useFormContext } from 'react-hook-form';

interface Props {
  onSectionClick: (sectionId: SectionId) => void;
  stepId?: string;
}

export const WizardProgressBar = ({ onSectionClick, stepId }: Props) => {
  const applicationContext = useApplicationContext();
  const { watch } = useFormContext();
  const { sections } = getOverviewFromApplication(applicationContext, watch(FIELD_IDS.REQUEST_ZILLOW_PAYMENTS));
  const mapSectionsToProgressSteps = useCallback(
    ({ label, appearance }: { id: string; label: string; appearance?: string }) => ({
      label,
      interactive: true,
      appearance,
    }),
    [],
  );

  function getSectionAppearance(completionStatus: REVIEW_STATUS) {
    if (completionStatus === REVIEW_STATUS.COMPLETE) {
      return 'complete';
    }
    return undefined;
  }

  const mappedSections = [
    ...sections.map((section) => ({
      id: section.id,
      label: section.label,
      appearance: getSectionAppearance(section.status),
    })),
    {
      id: SECTION_IDS.REVIEW,
      label: 'Review',
      appearance: undefined,
    },
  ];

  const activeSectionIndex = getSectionIndex(stepId, applicationContext.featureFlags);

  function handleSectionClick(_: React.MouseEventHandler, sectionIndex: number) {
    const section = sections.find((__, index) => sectionIndex === index);
    onSectionClick(section ? section.id : SECTION_IDS.REVIEW);
  }

  return (
    <ProgressStepper
      activeStepIndex={activeSectionIndex}
      onStepClick={handleSectionClick}
      mapStepsToProps={mapSectionsToProgressSteps}
      steps={mappedSections}
      // This is needed to fix the double firing of onStepClick
      // constellation-10 will have this fixed ideally
      // https://zillowgroup.atlassian.net/browse/CDS-3712
      renderProgressStepperStep={(props: ProgressStepperStepProps) => (
        <ProgressStepperStep
          {...props}
          renderLabel={(renderLabelProps: object) => (
            <ProgressStepperStepLabel {...renderLabelProps} onClick={undefined} />
          )}
        />
      )}
      marginY="md"
    />
  );
};
